/**
 * "Waiting on <person>" for a live item (case round, finding 4).
 *
 * The item's posture (work_state) has no "waiting on a colleague" value, so
 * an item Jess parked until Cliff rules sat under "Waiting on your call" and
 * read as if Jess were the blocker. The posture's one-line note is free text
 * that everyone sees, so "waiting on" is written there in one fixed shape —
 * `Waiting on Cliff Platt until 2026-10-09 — credit ruling` — with a parked
 * posture, and read back from it for the header, the list card and the
 * Triage lanes.
 *
 * Card a095e349 ("there should be a proper field"): the wait is now a
 * record on the item (`thread.wait`, services/signal_threads/waits.py),
 * which the screen reads first. The note is read only for an item that
 * never had a record — waits written before the record existed, until the
 * backfill converts them — so nothing disappears on release day.
 *
 * Pure logic (repo convention: logic in .ts, components in .tsx).
 */
import type { DoorThreadRead, ThreadWorkState } from '../../services/qcpInvestigations';

export interface WaitingOn {
  who: string;
  /** yyyy-mm-dd, or null when no date was given. */
  until: string | null;
  note: string;
  /** When the wait started (ISO): a response before it is not an answer. */
  since: string | null;
  /** 'record': thread.wait; 'note': a legacy posture note. */
  source: 'record' | 'note';
  /** The record's state ('waiting', or 'answered' until it is seen);
   *  'waiting' for a legacy note. */
  state: 'waiting' | 'answered';
}

type WaitFields = Pick<DoorThreadRead, 'work_state' | 'work_state_note' | 'status'> &
  Partial<Pick<DoorThreadRead, 'work_state_changed_at' | 'wait'>>;

const PARKED: ReadonlyArray<ThreadWorkState> = ['on_hold', 'awaiting_evidence'];
const SHAPE = /^Waiting on (.+?)(?: until (\d{4}-\d{2}-\d{2}))?(?: — ([\s\S]*))?$/;

/**
 * Who the item waits on; null otherwise. The record first: a wait still
 * waiting, or answered and not yet seen (the item stays in the waiting lane
 * with "responded" on it until the reader acknowledges, as before). Only an
 * item that never had a record falls back to a legacy posture note.
 */
export function waitingOn(thread: WaitFields): WaitingOn | null {
  if (thread.status !== 'routed' && thread.status !== 'open') return null;
  if (thread.wait) {
    const w = thread.wait;
    if (w.state !== 'waiting' && w.state !== 'answered') return null;
    return {
      who: w.who_name,
      until: w.until,
      note: w.reason,
      since: w.set_at,
      source: 'record',
      state: w.state,
    };
  }
  if (!PARKED.includes(thread.work_state)) return null;
  const m = SHAPE.exec((thread.work_state_note ?? '').trim());
  if (!m || !m[1].trim()) return null;
  return {
    who: m[1].trim(),
    until: m[2] ?? null,
    note: (m[3] ?? '').trim(),
    since: thread.work_state_changed_at ?? null,
    source: 'note',
    state: 'waiting',
  };
}

/** "Waiting on Cliff Platt · until Fri, Oct 9" / "… · was due Oct 9". */
export function waitingOnLabel(w: Pick<WaitingOn, 'who' | 'until'>, now: Date = new Date()): string {
  if (!w.until) return `Waiting on ${w.who}`;
  const day = untilDay(w.until);
  return waitingOverdue(w, now) ? `Waiting on ${w.who} · was due ${day}` : `Waiting on ${w.who} · until ${day}`;
}

/** Past its date: it is back on the reader to chase. */
export function waitingOverdue(w: Pick<WaitingOn, 'until'>, now: Date = new Date()): boolean {
  return daysOverdue(w, now) > 0;
}

const DAY_MS = 86_400_000;

function dueDate(until: string): Date {
  const [y, m, d] = until.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Whole days past the "until" date; 0 when not past it or undated. */
export function daysOverdue(w: Pick<WaitingOn, 'until'>, now: Date = new Date()): number {
  if (!w.until) return 0;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((today.getTime() - dueDate(w.until).getTime()) / DAY_MS));
}

/** "Wed, Sep 30" for an until date. */
export function untilDay(until: string): string {
  return dueDate(until).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

/** The waiting order (Triage's waiting lane and filter): past their date
 *  first (longest overdue on top), then the rest by date (soonest first),
 *  then the ones with no date (waiting longest first). */
export function waitingOrder<T extends WaitFields>(
  threads: T[],
  now: Date = new Date(),
  /** When an item waiting only on an open ask sent it (askWaitView); such
   *  an item sorts with the undated waits, by that time. */
  askedSince: (t: T) => string | null | undefined = () => undefined,
): T[] {
  const rank = (t: T): [number, number] => {
    const w = waitingOn(t);
    if (!w) {
      const asked = askedSince(t);
      if (asked === undefined) return [3, 0];
      const at = asked ? new Date(asked).getTime() : NaN;
      return [2, Number.isNaN(at) ? 0 : at];
    }
    if (w.until) return [waitingOverdue(w, now) ? 0 : 1, dueDate(w.until).getTime()];
    const since = w.since ? new Date(w.since).getTime() : 0;
    return [2, Number.isNaN(since) ? 0 : since];
  };
  return threads
    .map((t, i) => ({ t, i, r: rank(t) }))
    .sort((a, b) => a.r[0] - b.r[0] || a.r[1] - b.r[1] || a.i - b.i)
    .map(({ t }) => t);
}
